import type { ReactNode } from "react";

import type { PfCorrectionResponse } from "../services/pfCorrection";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { checkTone } from "./statusTone";
import { CASE_LABELS, FAULT_TYPE_LABELS } from "./starterCoordinationDraft";
import { LOAD_SOURCE_LABELS, SHORT_CIRCUIT_SOURCE_LABELS } from "./pfCorrectionLabels";

interface PfCorrectionResultPanelProps {
  result: PfCorrectionResponse;
  /** The engine that produced a stored run; names the engine behind a field it did not record. */
  engineVersion?: string;
}

// The four words of the Result column: a check is true (passed), false (a review - the
// capacitor voltage above its rating is never a pass on margin while the permissible
// overvoltage is unregistered, R1; a declared bank below the requirement is a warning) or
// null: not made (no bank declared, no short-circuit power).
export const RESULT_WORDS = {
  pass: "Pass",
  review: "Review",
  warning: "Warning",
  notEvaluated: "Not evaluated",
} as const;

type FalseMeaning = "review" | "warning";

function resultWord(check: boolean | null, whenFalse: FalseMeaning): string {
  if (check === null) {
    return RESULT_WORDS.notEvaluated;
  }
  return check ? RESULT_WORDS.pass : RESULT_WORDS[whenFalse];
}

function resultKey(check: boolean | null, whenFalse: FalseMeaning): string {
  if (check === null) {
    return "not-evaluated";
  }
  return check ? "pass" : whenFalse;
}

// One cell holding an engineering figure: four significant figures with the unit on screen,
// the exact value in the tooltip (display rule A12); an em dash for none.
function Quantity({ value, unit }: { value: string | null | undefined; unit: string }) {
  const quantity = formatQuantity(value ?? null, unit);
  return <td title={quantity.exact ?? undefined}>{quantity.display}</td>;
}

function Row({ name, label, children }: { name: string; label: string; children: ReactNode }) {
  return (
    <tr data-row-key={name}>
      <th scope="row">{label}</th>
      {children}
    </tr>
  );
}

function QuantityRow({
  name,
  label,
  value,
  unit,
}: {
  name: string;
  label: string;
  value: string | null | undefined;
  unit: string;
}) {
  return (
    <Row name={name} label={label}>
      <Quantity value={value} unit={unit} />
    </Row>
  );
}

function TextRow({ name, label, value }: { name: string; label: string; value: ReactNode }) {
  return (
    <Row name={name} label={label}>
      <td>{value ?? EMPTY_QUANTITY}</td>
    </Row>
  );
}

function ValueTable({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title}>
      <h3>{title}</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Parameter</th>
              <th scope="col">Value</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </section>
  );
}

// The load figures as the engine saw them, with where they came from: the linked EOS-02 run
// (group, revision) or the typed value with its source text.
function LoadTable({ result }: { result: PfCorrectionResponse }) {
  const linked = result.linked_load_run;
  return (
    <ValueTable title="Load figures">
      <TextRow name="load_source" label="Load figures source" value={LOAD_SOURCE_LABELS[result.load_source]} />
      <TextRow
        name="linked_load_run"
        label="Load run"
        value={linked ? `${linked.group_code} rev ${linked.revision_number}` : EMPTY_QUANTITY}
      />
      <TextRow name="load_source_text" label="Source" value={result.load_source_text ?? EMPTY_QUANTITY} />
      <QuantityRow name="active_power_kw" label="Active power P (kW)" value={result.active_power_kw} unit="kW" />
      <TextRow name="present_power_factor" label="Present power factor" value={result.present_power_factor} />
      <TextRow name="target_power_factor" label="Target power factor" value={result.target_power_factor} />
      <QuantityRow name="system_nominal_voltage_v" label="System nominal voltage (V)" value={result.system_nominal_voltage_v} unit="V" />
      <QuantityRow name="frequency_hz" label="Frequency (Hz)" value={result.frequency_hz} unit="Hz" />
    </ValueTable>
  );
}

// The power triangle before and after (section 3, items 1 and 2): tan φ, S and Q.
function PowerTriangleTable({ result }: { result: PfCorrectionResponse }) {
  return (
    <ValueTable title="Power triangle">
      <TextRow name="tan_phi_present" label="tan φ (present)" value={result.tan_phi_present} />
      <TextRow name="tan_phi_target" label="tan φ (target)" value={result.tan_phi_target} />
      <QuantityRow name="apparent_power_kva_before" label="Apparent power before (kVA)" value={result.apparent_power_kva_before} unit="kVA" />
      <QuantityRow name="reactive_power_kvar_before" label="Reactive power before (kvar)" value={result.reactive_power_kvar_before} unit="kvar" />
      <QuantityRow name="required_compensation_kvar" label="Required compensation Qc (kvar)" value={result.required_compensation_kvar} unit="kvar" />
      <QuantityRow name="apparent_power_kva_after" label="Apparent power after (kVA)" value={result.apparent_power_kva_after} unit="kVA" />
      <QuantityRow name="reactive_power_kvar_after" label="Reactive power after (kvar)" value={result.reactive_power_kvar_after} unit="kvar" />
    </ValueTable>
  );
}

// The bank at the system voltage (item 3) and with the reactor (item 4, R1); the step layout
// of an automatic bank (D7) when steps were declared.
function BankTable({ result }: { result: PfCorrectionResponse }) {
  const layout = result.step_layout;
  return (
    <ValueTable title="Capacitor bank">
      <QuantityRow name="bank.rated_voltage_v" label="Capacitor rated voltage (V)" value={result.bank.rated_voltage_v} unit="V" />
      <TextRow name="voltage_ratio_squared" label="(U / Ur)²" value={result.voltage_ratio_squared} />
      <QuantityRow name="bank_rating_required_kvar" label="Rating required at Ur, no reactor (kvar)" value={result.bank_rating_required_kvar} unit="kvar" />
      <QuantityRow name="bank_rating_required_with_reactor_kvar" label="Rating required at Ur, with reactor (kvar)" value={result.bank_rating_required_with_reactor_kvar} unit="kvar" />
      <QuantityRow name="bank.declared_rating_kvar" label="Declared bank rating (kvar)" value={result.bank.declared_rating_kvar} unit="kvar" />
      <QuantityRow name="declared_bank_delivered_kvar" label="Declared bank delivers at the bus (kvar)" value={result.declared_bank_delivered_kvar} unit="kvar" />
      <TextRow
        name="bank.step_sizes_kvar"
        label="Step sizes (kvar)"
        value={result.bank.step_sizes_kvar.length > 0 ? result.bank.step_sizes_kvar.join(" · ") : EMPTY_QUANTITY}
      />
      <TextRow name="step_layout.smallest_step_share_percent" label="Smallest step share of Qc (%)" value={layout?.smallest_step_share_percent ?? EMPTY_QUANTITY} />
      <QuantityRow name="step_layout.reachable_kvar" label="Reachable with the steps (kvar)" value={layout?.reachable_kvar} unit="kvar" />
      <QuantityRow name="step_layout.residual_kvar" label="Residual after the steps (kvar)" value={layout?.residual_kvar} unit="kvar" />
    </ValueTable>
  );
}

// The detuned reactor (item 4, R1): the tuning order and the capacitor terminal voltage.
function ReactorTable({ result }: { result: PfCorrectionResponse }) {
  const reactor = result.reactor;
  return (
    <ValueTable title="Detuned reactor">
      <TextRow name="reactor_ratio_percent" label="Reactor ratio p (%)" value={result.reactor_ratio_percent ?? EMPTY_QUANTITY} />
      <TextRow name="reactor.tuning_order" label="Tuning order 1 / √p" value={reactor?.tuning_order ?? EMPTY_QUANTITY} />
      <QuantityRow name="reactor.capacitor_terminal_voltage_v" label="Capacitor terminal voltage Uc (V)" value={reactor?.capacitor_terminal_voltage_v} unit="V" />
    </ValueTable>
  );
}

// The short-circuit power and the resonance screening on the R2 basis (item 5, D6): where
// S_sc came from and how it was formed, h_r, h_p and the characteristic orders matched.
function ResonanceTable({ result }: { result: PfCorrectionResponse }) {
  const power = result.short_circuit_power;
  const resonance = result.resonance;
  const linked = power !== null && power.source === "LINKED_FAULT_RUN";
  return (
    <ValueTable title="Resonance screening">
      <TextRow name="short_circuit_power.source" label="Short-circuit power source" value={power ? SHORT_CIRCUIT_SOURCE_LABELS[power.source] : EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.fault_run_id" label="Fault run" value={power?.fault_run_id ?? EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.fault_bus_code" label="Fault bus" value={power?.fault_bus_code ?? EMPTY_QUANTITY} />
      <TextRow
        name="short_circuit_power.calculation_case"
        label="Calculation case"
        value={linked && power.calculation_case ? CASE_LABELS[power.calculation_case] : EMPTY_QUANTITY}
      />
      <TextRow
        name="short_circuit_power.fault_type"
        label="Fault type"
        value={linked && power.fault_type ? FAULT_TYPE_LABELS[power.fault_type] : EMPTY_QUANTITY}
      />
      <QuantityRow name="short_circuit_power.short_circuit_current_ka" label="Ik'' of the run (kA)" value={power?.short_circuit_current_ka} unit="kA" />
      <QuantityRow name="short_circuit_power.value_kva" label="Short-circuit power S_sc (kVA)" value={power?.value_kva} unit="kVA" />
      <TextRow name="short_circuit_power.basis" label="S_sc basis" value={power?.basis ?? EMPTY_QUANTITY} />
      <TextRow name="short_circuit_power.source_text" label="Source" value={power?.source_text ?? EMPTY_QUANTITY} />
      <TextRow
        name="characteristic_orders"
        label="Characteristic orders in the loads"
        value={result.characteristic_orders.length > 0 ? result.characteristic_orders.join(", ") : EMPTY_QUANTITY}
      />
      <TextRow name="resonance.resonance_order" label="Resonance order h_r" value={resonance?.resonance_order ?? EMPTY_QUANTITY} />
      <TextRow name="resonance.detuned_resonance_order" label="Detuned resonance order h_p" value={resonance?.detuned_resonance_order ?? EMPTY_QUANTITY} />
      <TextRow
        name="resonance.nearest_order"
        label="Nearest characteristic order"
        value={
          resonance?.nearest_order !== null && resonance?.nearest_order !== undefined
            ? `${resonance.nearest_order} (distance ${resonance.distance_to_nearest_order ?? EMPTY_QUANTITY})`
            : EMPTY_QUANTITY
        }
      />
      <TextRow
        name="resonance.matched_orders"
        label="Orders within ±10 % (KES screening rule)"
        value={
          resonance === null
            ? EMPTY_QUANTITY
            : resonance.matched_orders.length > 0
              ? resonance.matched_orders.join(", ")
              : "none"
        }
      />
    </ValueTable>
  );
}

type Check = {
  key: string;
  label: string;
  value: ReactNode;
  limit: ReactNode;
  check: boolean | null;
  whenFalse: FalseMeaning;
};

// Each check beside the figure it was held against: the declared bank against Qc, and the
// capacitor terminal voltage against the rated voltage (R1). Every figure is the engine's.
function ChecksTable({ result }: { result: PfCorrectionResponse }) {
  const nominalVoltageOnly = result.warnings.some(
    (warning) => warning.code === "CAPACITOR_VOLTAGE_AT_NOMINAL_ONLY",
  );
  const checks: Check[] = [
    {
      key: "bank-covers",
      label: "Declared bank covers Qc",
      value: <Quantity value={result.declared_bank_delivered_kvar} unit="kvar" />,
      limit: <Quantity value={result.required_compensation_kvar} unit="kvar" />,
      check: result.bank_covers_requirement,
      whenFalse: "warning",
    },
    {
      key: "capacitor-voltage",
      label: "Capacitor voltage within rating",
      value: (
        <Quantity
          value={result.reactor?.capacitor_terminal_voltage_v ?? result.system_nominal_voltage_v}
          unit="V"
        />
      ),
      // IR-005 finding F-1 part 1: the comparison is made at the nominal voltage only; the
      // rating cell says so whenever the result carries the information code.
      limit: (
        <td
          title={formatQuantity(result.bank.rated_voltage_v, "V").exact ?? undefined}
          data-nominal-voltage-only={nominalVoltageOnly}
        >
          {formatQuantity(result.bank.rated_voltage_v, "V").display}
          {nominalVoltageOnly ? " · at nominal voltage only" : ""}
        </td>
      ),
      check: result.reactor?.capacitor_voltage_within_rating ?? null,
      whenFalse: "review",
    },
    {
      key: "resonance",
      label: "Resonance clear of the characteristic orders",
      value: <td>{result.resonance?.resonance_order ?? EMPTY_QUANTITY}</td>,
      limit: (
        <td>
          {result.characteristic_orders.length > 0
            ? `±10 % of ${result.characteristic_orders.join(", ")}`
            : EMPTY_QUANTITY}
        </td>
      ),
      check:
        result.resonance === null || result.characteristic_orders.length === 0
          ? null
          : !result.resonance.near_characteristic_order,
      whenFalse: "warning",
    },
  ];

  return (
    <section aria-label="Checks">
      <h3>Checks</h3>
      <div data-table-scroll>
        <table>
          <thead>
            <tr>
              <th scope="col">Check</th>
              <th scope="col">Value</th>
              <th scope="col">Against</th>
              <th scope="col">Result</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((item) => {
              const key = resultKey(item.check, item.whenFalse);
              return (
                <tr key={item.key} data-check-key={item.key}>
                  <th scope="row">{item.label}</th>
                  {item.value}
                  {item.limit}
                  <td data-check-result={key}>
                    <StatusIcon tone={checkTone(key)} size={16} />
                    {resultWord(item.check, item.whenFalse)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// The detail tables of one power factor correction study (Master Prompt v2.1 section 19):
// the checks first, then the load figures, the power triangle, the bank, the reactor and the
// resonance screening, then the two fixed notes the engine carries (billing context, the
// displacement power factor basis). Nothing is recomputed here.
export function PfCorrectionResultPanel({ result }: PfCorrectionResultPanelProps) {
  return (
    <section aria-label="Result detail" data-result-status={result.status}>
      <ChecksTable result={result} />
      <LoadTable result={result} />
      <PowerTriangleTable result={result} />
      <BankTable result={result} />
      <ReactorTable result={result} />
      <ResonanceTable result={result} />
      <p data-billing-context="true">{result.billing_context}</p>
      <p data-power-factor-basis="true">{result.power_factor_basis}</p>
    </section>
  );
}

export default PfCorrectionResultPanel;
